import { Injectable, Injector, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

/**
 * Keeps long-lived sessions (installed PWAs, game tabs left open for days)
 * on the latest deployed build. The Angular service worker downloads new
 * versions in the background but never activates them on its own — without
 * this, users keep seeing the old cached app (and none of the new game
 * features) until they close every tab and do a double reload.
 */

// Periodic poll while the tab stays open.
const CHECK_EVERY_MS = 30 * 60 * 1000;
// Throttle for checks triggered by the tab becoming visible again.
const MIN_GAP_BETWEEN_CHECKS_MS = 5 * 60 * 1000;
// The prompt auto-dismisses so it can never sit on top of game UI forever;
// it is re-offered every time the user returns to the tab.
const PROMPT_DURATION_MS = 15 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly updates = inject(SwUpdate);
  private readonly injector = inject(Injector);

  private updateReady = false;
  private lastCheckedAt = 0;

  init(): void {
    if (!this.updates.isEnabled) {
      return;
    }

    this.updates.versionUpdates.subscribe((event) => {
      // Rare, high-signal events — logged so stale-client reports can be
      // diagnosed from the browser console.
      if (event.type === 'VERSION_DETECTED') {
        console.info(`[app-update] downloading: ${event.version.hash}`);
      } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
        console.error(`[app-update] install failed: ${event.error}`);
      }
    });

    this.updates.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe((event) => {
        console.info(
          `[app-update] new version ready: ${event.latestVersion.hash}`,
        );
        this.updateReady = true;
        this.promptToReload();
      });

    // The service worker can hit a state where files it needs are gone from
    // the cache (e.g. the browser evicted storage mid-update). Only a full
    // reload recovers from that.
    this.updates.unrecoverable.subscribe(async (event) => {
      console.error(`[app-update] unrecoverable SW state: ${event.reason}`);
      const snackBar = await this.getSnackBar();
      snackBar.open('Reloading to update the app…', undefined, {
        duration: 3000,
      });
      setTimeout(() => document.location.reload(), 3000);
    });

    // The browser only re-fetches ngsw.json on full page loads, and installed
    // PWAs / game tabs are often left running for days — poll so those
    // sessions still learn about new deploys.
    setInterval(() => this.checkForUpdate(), CHECK_EVERY_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (this.updateReady) {
        this.promptToReload();
      } else if (Date.now() - this.lastCheckedAt > MIN_GAP_BETWEEN_CHECKS_MS) {
        this.checkForUpdate();
      }
    });
  }

  private checkForUpdate(): void {
    this.lastCheckedAt = Date.now();
    this.updates
      .checkForUpdate()
      .then((found) => console.info(`[app-update] checked, update=${found}`))
      .catch((err) =>
        // Offline or the worker is not registered yet — the next poll retries.
        console.info(`[app-update] check skipped: ${err}`),
      );
  }

  private async promptToReload(): Promise<void> {
    const snackBar = await this.getSnackBar();
    const ref = snackBar.open('A new version is available', 'Update', {
      duration: PROMPT_DURATION_MS,
      panelClass: 'app-update-snackbar',
    });
    ref.onAction().subscribe(() => document.location.reload());
  }

  // Loaded on demand so Material's snackbar + overlay stack stays out of the
  // initial bundle (~118 kB raw) — it is only needed once a new version is
  // actually waiting.
  private async getSnackBar() {
    const { MatSnackBar } = await import('@angular/material/snack-bar');
    return this.injector.get(MatSnackBar);
  }
}
